'use client';

import { useField } from 'formik';
import {
  Box,
  Radio,
  FormLabel,
  RadioGroup,
  FormControl,
  FormHelperText,
  RadioGroupProps,
  FormControlLabel,
} from '@mui/material';

import { controlError } from 'utils/error';
import { QuestionLabel } from 'components/question-label';

export interface RadioFieldProps extends Omit<
  RadioGroupProps,
  'name' | 'onChange'
> {
  name: string;
  label?: string;
  fullWidth?: boolean;
  withBorder?: boolean;
  controlErrorLabel?: boolean;
  displayValue?: string | string[];
  mapValue?: (value: string) => unknown;
  options: { label: string; value: string | number }[];
  onChange?: (
    event: React.ChangeEvent<HTMLInputElement>,
    value: string
  ) => void;
}

export const RadioField = ({
  sx,
  name,
  label,
  options,
  onChange,
  mapValue,
  fullWidth,
  withBorder,
  displayValue,
  ...props
}: RadioFieldProps) => {
  const [field, meta, helpers] = useField(name);

  const errorText = controlError(meta, name, label);
  const hasError = Boolean(errorText);

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    helpers.setValue(mapValue ? mapValue(value) : value, true);
    onChange?.(event, value);
  };

  return (
    <FormControl error={hasError}>
      {label && (
        <FormLabel component="div">
          <QuestionLabel value={label} />
        </FormLabel>
      )}
      <RadioGroup
        {...props}
        name={field.name}
        onChange={handleChange}
        value={displayValue ?? field.value}
        sx={{
          ...(fullWidth && { flexWrap: 'nowrap' }),
          ...sx,
        }}
      >
        {options.map((opt) => (
          <Box
            key={opt.value}
            sx={{
              ...(fullWidth && { width: '100%' }),
              ...(withBorder
                ? {
                    padding: '4px 8px',
                    border: '1px solid',
                    borderRadius: '8px',
                    borderColor: 'primary.main',
                    '&:hover': {
                      borderColor: 'primary.dark',
                    },
                    '&.selected': {
                      borderColor: 'primary.dark',
                    },
                  }
                : {}),
            }}
          >
            <FormControlLabel
              value={opt.value}
              label={opt.label}
              control={<Radio />}
            />
          </Box>
        ))}
      </RadioGroup>
      {hasError && (
        <FormHelperText className="!ml-0">{errorText}</FormHelperText>
      )}
    </FormControl>
  );
};
